'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useAnimation } from '@/context/AnimationContext';

/**
 * ScrollReveal - Animate elements on scroll (once)
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to animate
 * @param {'left' | 'right' | 'bottom' | 'top'} props.direction - Animation direction
 * @param {number} props.delay - Delay in seconds (default: 0)
 * @param {number} props.duration - Animation duration (default: 0.5)
 * @param {string} props.className - Additional CSS classes
 */
export default function ScrollReveal({
    children,
    direction = 'bottom',
    delay = 0,
    duration = 0.5,
    className = ''
}) {
    const ref = useRef(null);
    const { isMobile, shouldAnimate } = useAnimation();
    const isInView = useInView(ref, {
        once: true,           // Animate only once
        amount: 0.2,          // Trigger when 20% visible
        margin: '0px 0px -50px 0px'  // Trigger slightly before entering viewport
    });

    // Define animation variants based on direction
    const variants = {
        left: {
            hidden: { opacity: 0, x: -60 },
            visible: { opacity: 1, x: 0 }
        },
        right: {
            hidden: { opacity: 0, x: 60 },
            visible: { opacity: 1, x: 0 }
        },
        bottom: {
            hidden: { opacity: 0, y: 60 },
            visible: { opacity: 1, y: 0 }
        },
        top: {
            hidden: { opacity: 0, y: -60 },
            visible: { opacity: 1, y: 0 }
        }
    };

    // Animation Logic:
    // Mobile: Render visible immediately (no animation).
    // Desktop: Wait for shouldAnimate (network idle) AND isInView.

    // We can disable animation by setting initial/animate to "visible" directly or passing null vars.
    const isReadyToAnimate = isMobile || (shouldAnimate && isInView);

    return (
        <motion.div
            ref={ref}
            initial={isMobile ? "visible" : "hidden"}
            animate={isReadyToAnimate ? "visible" : "hidden"}
            variants={variants[direction]}
            transition={{
                duration,
                delay,
                ease: [0.25, 0.4, 0.25, 1] // Smooth easing
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

/**
 * ScrollRevealStagger - Stagger animation for multiple children
 * @param {Object} props
 * @param {React.ReactNode} props.children - Array of children to stagger
 * @param {'left' | 'right' | 'bottom'} props.direction - Animation direction
 * @param {number} props.staggerDelay - Delay between each child (default: 0.1)
 */
export function ScrollRevealStagger({
    children,
    direction = 'bottom',
    staggerDelay = 0.1,
    className = ''
}) {
    const ref = useRef(null);
    const isInView = useInView(ref, {
        once: true,
        amount: 0.1
    });

    return (
        <div ref={ref} className={className}>
            {Array.isArray(children) ? (
                children.map((child, index) => (
                    <ScrollReveal
                        key={index}
                        direction={direction}
                        delay={index * staggerDelay}
                    >
                        {child}
                    </ScrollReveal>
                ))
            ) : (
                <ScrollReveal direction={direction}>
                    {children}
                </ScrollReveal>
            )}
        </div>
    );
}
